import {
  Connection,
  LAMPORTS_PER_SOL,
  ParsedAccountData,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";

import {
  setComputeUnitLimit,
  setComputeUnitPrice,
  transferSol,
} from "@metaplex-foundation/mpl-toolbox";
import {
  publicKey,
  sol,
  transactionBuilder,
  Umi,
} from "@metaplex-foundation/umi";
import {
  toWeb3JsLegacyTransaction,
  toWeb3JsTransaction,
} from "@metaplex-foundation/umi-web3js-adapters";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createTransferCheckedInstruction,
  getMint,
  Mint,
  MintLayout,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { WalletContextState } from "@solana/wallet-adapter-react";
import { ComputeBudgetProgram, VersionedTransaction } from "@solana/web3.js";
import {
  BASE_FEE,
  REFUND_WALLET,
  SOL_ADDRESS,
  TREASURY_WALLET,
} from "../config";
import { BundleType, NftDataType, PriceType } from "./types";

export const METAPLEX = new PublicKey(
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s"
);
export const getMintInfo = async (
  mintAddress: PublicKey,
  connection: Connection
): Promise<Mint> => {
  let mintInfo = await getMint(
    connection,
    mintAddress,
    "singleGossip",
    TOKEN_PROGRAM_ID
  );
  return mintInfo;

  // const mintAccountInfo = await connection.getAccountInfo(mintAddress);
  // if (!mintAccountInfo) {
  //   throw new Error("Mint account not found");
  // }

  // const buffer = Buffer.from(mintAccountInfo.data);
  // const data = MintLayout.decode(buffer);

  // return {
  //   mintAuthority: data.mintAuthority,
  //   supply: data.supply,
  //   decimals: data.decimals,
  //   isInitialized: data.isInitialized,
  //   freezeAuthority: data.freezeAuthority,
  // };
};

export const getOwnerOfNFT = async (
  nftMintPk: PublicKey,
  connection: Connection
): Promise<PublicKey> => {
  let tokenAccountPK = await getNFTTokenAccount(nftMintPk, connection);
  let tokenAccountInfo = await connection.getAccountInfo(tokenAccountPK);

  //console.log("nftMintPk=", nftMintPk.toBase58());
  //console.log("tokenAccountInfo =", tokenAccountInfo);

  if (tokenAccountInfo && tokenAccountInfo.data) {
    let ownerPubkey = new PublicKey(tokenAccountInfo.data.slice(32, 64));
    //console.log("ownerPubkey=", ownerPubkey.toBase58());
    return ownerPubkey;
  }
  return new PublicKey("");
};

export const getTokenAccount = async (
  mintPk: PublicKey,
  userPk: PublicKey,
  connection: Connection
): Promise<PublicKey> => {
  let tokenAccount = await connection.getProgramAccounts(TOKEN_PROGRAM_ID, {
    filters: [
      {
        dataSize: 165,
      },
      {
        memcmp: {
          offset: 0,
          bytes: mintPk.toBase58(),
        },
      },
      {
        memcmp: {
          offset: 32,
          bytes: userPk.toBase58(),
        },
      },
    ],
  });
  return tokenAccount[0].pubkey;
};

export const getNFTTokenAccount = async (
  nftMintPk: PublicKey,
  connection: Connection
): Promise<PublicKey> => {
  //console.log("getNFTTokenAccount nftMintPk=", nftMintPk.toBase58());
  let tokenAccount = await connection.getProgramAccounts(TOKEN_PROGRAM_ID, {
    filters: [
      {
        dataSize: 165,
      },
      {
        memcmp: {
          offset: 64,
          bytes: "2",
        },
      },
      {
        memcmp: {
          offset: 0,
          bytes: nftMintPk.toBase58(),
        },
      },
    ],
  });
  return tokenAccount[0].pubkey;
};

export const getAssociatedTokenAccount = async (
  ownerPubkey: PublicKey,
  mintPk: PublicKey
): Promise<PublicKey> => {
  let associatedTokenAccountPubkey = (
    await PublicKey.findProgramAddressSync(
      [
        ownerPubkey.toBuffer(),
        TOKEN_PROGRAM_ID.toBuffer(),
        mintPk.toBuffer(), // mint address
      ],
      ASSOCIATED_TOKEN_PROGRAM_ID
    )
  )[0];
  return associatedTokenAccountPubkey;
};

export const getATokenAccountsNeedCreate = async (
  connection: Connection,
  walletAddress: PublicKey,
  owner: PublicKey,
  nfts: PublicKey[]
) => {
  let instructions = [],
    destinationAccounts = [];
  for (const mint of nfts) {
    const destinationPubkey = await getAssociatedTokenAccount(owner, mint);
    const response = await connection.getAccountInfo(destinationPubkey);
    if (!response) {
      const createATAIx = createAssociatedTokenAccountInstruction(
        destinationPubkey,
        walletAddress,
        owner,
        mint
      );
      instructions.push(createATAIx);
    }
    destinationAccounts.push(destinationPubkey);
  }
  return {
    instructions,
    destinationAccounts,
  };
};

export const createAssociatedTokenAccountInstruction = (
  associatedTokenAddress: PublicKey,
  payer: PublicKey,
  walletAddress: PublicKey,
  splTokenMintAddress: PublicKey
) => {
  const keys = [
    { pubkey: payer, isSigner: true, isWritable: true },
    { pubkey: associatedTokenAddress, isSigner: false, isWritable: true },
    { pubkey: walletAddress, isSigner: false, isWritable: false },
    { pubkey: splTokenMintAddress, isSigner: false, isWritable: false },
    {
      pubkey: SystemProgram.programId,
      isSigner: false,
      isWritable: false,
    },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    {
      pubkey: SYSVAR_RENT_PUBKEY,
      isSigner: false,
      isWritable: false,
    },
  ];
  return new TransactionInstruction({
    keys,
    programId: ASSOCIATED_TOKEN_PROGRAM_ID,
    data: Buffer.from([]),
  });
};

/** Get metaplex mint metadata account address */
export const getMetadata = async (mint: PublicKey): Promise<PublicKey> => {
  return (
    await PublicKey.findProgramAddressSync(
      [Buffer.from("metadata"), METAPLEX.toBuffer(), mint.toBuffer()],
      METAPLEX
    )
  )[0];
};

/* export const airdropSOL = async (
  address: PublicKey,
  amount: number,
  connection: Connection
) => {
  try {
    const txId = await connection.requestAirdrop(address, amount);
    await connection.confirmTransaction(txId);
  } catch (e) {
    console.log("Aridrop Failure", address.toBase58(), amount);
  }
}; */

export const isExistAccount = async (
  address: PublicKey,
  connection: Connection
) => {
  try {
    const res = await connection.getAccountInfo(address);
    if (res && res.data) return true;
  } catch (e) {
    return false;
  }
};

export const getTokenAccountBalance = async (
  account: PublicKey,
  connection: Connection
) => {
  try {
    const res = await connection.getTokenAccountBalance(account);
    if (res && res.value) return res.value.uiAmount;
    return 0;
  } catch (e) {
    console.log(e);
    return 0;
  }
};

export const filterNftsByCreator = (
  nfts: NftDataType[],
  creator: PublicKey
): NftDataType[] => {
  return nfts
    .filter((nft) => nft.creator == creator.toString())
    .sort((item1, item2) => Number(item1.nftname) - Number(item2.nftname));
};

export const filterNftsBySymbol = (
  nfts: NftDataType[],
  symbol: string
): NftDataType[] => {
  return nfts
    .filter((nft) => nft.symbol?.toLowerCase() == symbol.toLowerCase())
    .sort((item1, item2) => Number(item1.nftname) - Number(item2.nftname));
};

export const filterNftsByCollection = (
  nfts: NftDataType[],
  collection: string
): NftDataType[] => {
  return nfts
    .filter((nft) => nft.collection?.toLowerCase() == collection.toLowerCase())
    .sort((item1, item2) => Number(item1.nftname) - Number(item2.nftname));
};

export const checkTokenAddressValidateOnchain = async (
  mintAddress: string,
  solConnection: Connection
) => {
  const mintAccountInfo = await solConnection.getAccountInfo(
    new PublicKey(mintAddress)
  );
  if (!mintAccountInfo) {
    throw new Error("Mint account not found");
  }

  const buffer = mintAccountInfo.data;
  const uint8Array: Uint8Array = new Uint8Array(buffer);
  const data = MintLayout.decode(uint8Array);
  if (data && data.supply && data.decimals && data.isInitialized) {
    return true;
  } else {
    return false;
  }
};

export const createBundleDiscountPurchaseTx = async (
  bundle: BundleType,
  code: any,
  umi: Umi,
  wallet: WalletContextState,
  solConnection: Connection
) => {
  try {
    if (!bundle.token) {
      return {
        result: false,
        data: "Invalid purchase info",
      };
    }
    if (!wallet.publicKey || !wallet.connected) {
      return {
        result: false,
        data: "Wallet Connection Issue",
      };
    }
    let purchaseTx;

    if (bundle.token.address == SOL_ADDRESS.toBase58()) {
      let balance = await solConnection.getBalance(wallet.publicKey);
      if (
        balance <
        (bundle.price - bundle.price * (code.discount / 100)) * LAMPORTS_PER_SOL
      ) {
        return {
          result: false,
          data: "Invalid SOL balance",
        };
      }
      let tx = await createSolTransferTx(
        bundle.price - bundle.price * (code.discount / 100),
        umi,
        wallet,
        solConnection
      );
      if (!tx) {
        return {
          result: false,
          data: "Invalid Connection",
        };
      } else {
        //console.log(" sol transfer tx ", tx)
        //console.log(" price ", price.price)
        // purchaseTx = tx;
        return {
          result: true,
          data: tx,
        };
      }
    } else {
      let tokenBalance = await getTokenBalance(
        new PublicKey(bundle.token.address),
        wallet.publicKey,
        solConnection
      );
      if (!tokenBalance) {
        return {
          result: false,
          data: `Could Not Find Associated Token Account!\nPlease certify that you have ${bundle.token.name} in your wallet`,
        };
      }
      let tokenDecimal = await getTokenDecimal(
        new PublicKey(bundle.token.address),
        solConnection
      );
      //console.log(">> token balance ", tokenBalance),
      //console.log(">> token info ", price.price, tokenDecimal, price.price * Math.pow(10, tokenDecimal))
      if (tokenBalance < bundle.price * (code.discount / 100)) {
        return {
          result: false,
          data: "Low Token balance",
        };
      }
      purchaseTx = await createTokenTransferTx(
        solConnection,
        wallet.publicKey,
        TREASURY_WALLET,
        new PublicKey(bundle.token.address),
        (bundle.price - bundle.price * (code.discount / 100)) *
          Math.pow(10, tokenDecimal)
      );
      return {
        result: true,
        data: purchaseTx,
      };
    }
  } catch (e) {
    console.log(e);
    return {
      result: false,
      data: "Connection Exception Occured",
    };
  }
};

export const createDiscountPurchaseTx = async (
  price: PriceType,
  code: any,
  umi: Umi,
  wallet: WalletContextState,
  solConnection: Connection
) => {
  try {
    if (!price.token) {
      return {
        result: false,
        data: "Invalid purchase info",
      };
    }
    if (!wallet.publicKey || !wallet.connected) {
      return {
        result: false,
        data: "Wallet Connection Issue",
      };
    }
    let purchaseTx;

    if (price.token.address == SOL_ADDRESS.toBase58()) {
      let balance = await solConnection.getBalance(wallet.publicKey);
      if (
        balance <
        (price.price - price.price * (code.discount / 100)) * LAMPORTS_PER_SOL
      ) {
        return {
          result: false,
          data: "Invalid SOL balance",
        };
      }
      let tx = await createSolTransferTx(
        price.price - price.price * (code.discount / 100),
        umi,
        wallet,
        solConnection
      );
      if (!tx) {
        return {
          result: false,
          data: "Invalid Connection",
        };
      } else {
        //console.log(" sol transfer tx ", tx)
        //console.log(" price ", price.price)
        // purchaseTx = tx;
        return {
          result: true,
          data: tx,
        };
      }
    } else {
      let tokenBalance = await getTokenBalance(
        new PublicKey(price.token.address),
        wallet.publicKey,
        solConnection
      );
      if (!tokenBalance) {
        return {
          result: false,
          data: `Could Not Find Associated Token Account!\nPlease certify that you have ${price.token.name} in your wallet`,
        };
      }
      let tokenDecimal = await getTokenDecimal(
        new PublicKey(price.token.address),
        solConnection
      );
      //console.log(">> token balance ", tokenBalance),
      //console.log(">> token info ", price.price, tokenDecimal, price.price * Math.pow(10, tokenDecimal))
      if (tokenBalance < price.price * (code.discount / 100)) {
        return {
          result: false,
          data: "Low Token balance",
        };
      }
      purchaseTx = await createTokenTransferTx(
        solConnection,
        wallet.publicKey,
        TREASURY_WALLET,
        new PublicKey(price.token.address),
        (price.price - price.price * (code.discount / 100)) *
          Math.pow(10, tokenDecimal)
      );
      return {
        result: true,
        data: purchaseTx,
      };
    }
  } catch (e) {
    console.log(e);
    return {
      result: false,
      data: "Connection Exception Occured",
    };
  }
};

export const createBundlePurchaseTx = async (
  bundle: BundleType,
  umi: Umi,
  wallet: WalletContextState,
  solConnection: Connection
) => {
  try {
    if (!bundle.token) {
      return {
        result: false,
        data: "Invalid purchase info",
      };
    }
    if (!wallet.publicKey || !wallet.connected) {
      return {
        result: false,
        data: "Wallet Connection Issue",
      };
    }
    let purchaseTx;

    if (bundle.token.address == SOL_ADDRESS.toBase58()) {
      let balance = await solConnection.getBalance(wallet.publicKey);
      if (balance < bundle.price * LAMPORTS_PER_SOL) {
        return {
          result: false,
          data: "Invalid SOL balance",
        };
      }
      let tx = await createSolTransferTx(
        bundle.price,
        umi,
        wallet,
        solConnection
      );
      if (!tx) {
        return {
          result: false,
          data: "Invalid Connection",
        };
      } else {
        //console.log(" sol transfer tx ", tx)
        //console.log(" price ", price.price)
        // purchaseTx = tx;
        return {
          result: true,
          data: tx,
        };
      }
    } else {
      let tokenBalance = await getTokenBalance(
        new PublicKey(bundle.token.address),
        wallet.publicKey,
        solConnection
      );
      if (!tokenBalance) {
        return {
          result: false,
          data: `Could Not Find Associated Token Account!\nPlease certify that you have ${bundle.token.name} in your wallet`,
        };
      }
      let tokenDecimal = await getTokenDecimal(
        new PublicKey(bundle.token.address),
        solConnection
      );
      //console.log(">> token balance ", tokenBalance),
      //console.log(">> token info ", price.price, tokenDecimal, price.price * Math.pow(10, tokenDecimal))
      if (tokenBalance < bundle.price) {
        return {
          result: false,
          data: "Low Token balance",
        };
      }
      purchaseTx = await createTokenTransferTx(
        solConnection,
        wallet.publicKey,
        TREASURY_WALLET,
        new PublicKey(bundle.token.address),
        bundle.price * Math.pow(10, tokenDecimal)
      );
      return {
        result: true,
        data: purchaseTx,
      };
    }
  } catch (e) {
    console.log(e);
    return {
      result: false,
      data: "Connection Exception Occured",
    };
  }
};

export const createPurchaseTx = async (
  price: PriceType,
  umi: Umi,
  wallet: WalletContextState,
  solConnection: Connection
) => {
  try {
    if (!price.token) {
      return {
        result: false,
        data: "Invalid purchase info",
      };
    }
    if (!wallet.publicKey || !wallet.connected) {
      return {
        result: false,
        data: "Wallet Connection Issue",
      };
    }
    let purchaseTx;

    if (price.token.address == SOL_ADDRESS.toBase58()) {
      let balance = await solConnection.getBalance(wallet.publicKey);
      if (balance < price.price * LAMPORTS_PER_SOL) {
        return {
          result: false,
          data: "Invalid SOL balance",
        };
      }
      let tx = await createSolTransferTx(
        price.price,
        umi,
        wallet,
        solConnection
      );
      if (!tx) {
        return {
          result: false,
          data: "Invalid Connection",
        };
      } else {
        //console.log(" sol transfer tx ", tx)
        //console.log(" price ", price.price)
        // purchaseTx = tx;
        return {
          result: true,
          data: tx,
        };
      }
    } else {
      let tokenBalance = await getTokenBalance(
        new PublicKey(price.token.address),
        wallet.publicKey,
        solConnection
      );
      if (!tokenBalance) {
        return {
          result: false,
          data: `Could Not Find Associated Token Account!\nPlease certify that you have ${price.token.name} in your wallet`,
        };
      }
      let tokenDecimal = await getTokenDecimal(
        new PublicKey(price.token.address),
        solConnection
      );
      //console.log(">> token balance ", tokenBalance),
      //console.log(">> token info ", price.price, tokenDecimal, price.price * Math.pow(10, tokenDecimal))
      if (tokenBalance < price.price) {
        return {
          result: false,
          data: "Low Token balance",
        };
      }
      purchaseTx = await createTokenTransferTx(
        solConnection,
        wallet.publicKey,
        TREASURY_WALLET,
        new PublicKey(price.token.address),
        price.price * Math.pow(10, tokenDecimal)
      );
      return {
        result: true,
        data: purchaseTx,
      };
    }
  } catch (e) {
    console.log(e);
    return {
      result: false,
      data: "Connection Exception Occured",
    };
  }
};

export const createTokenTransferTx = async (
  connection: Connection,
  owner: PublicKey,
  treasury: PublicKey,
  mint: PublicKey,
  amount: number
) => {
  //Task : Try Umifying This thing from Tx=NewTrainsaction
  let decimal = await getTokenDecimal(mint, connection);

  let userTokenAccount = await getAssociatedTokenAccount(owner, mint);

  let tx = new Transaction();

  let ret = await getATokenAccountsNeedCreate(
    connection,
    owner,
    REFUND_WALLET,
    [mint]
  );
  tx.add(
    ComputeBudgetProgram.setComputeUnitLimit({
      units: 1_400_000,
    })
  );

  tx.add(
    ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 500_000,
    })
  );
  if (ret && ret.instructions && ret.instructions.length > 0) {
    ret.instructions.map((ix) => tx.add(ix));
  }
  /* let baseFee = SystemProgram.transfer(
    {
      "fromPubkey": owner,
      // "lamports": parseFloat(BASE_FEE) * LAMPORTS_PER_SOL,
      "lamports": (BASE_FEE - 0.001) * LAMPORTS_PER_SOL,
      "toPubkey": TREASURY_WALLET
    }); */
  let refundFee = SystemProgram.transfer({
    fromPubkey: owner,
    // "lamports": parseFloat(BASE_FEE) * LAMPORTS_PER_SOL,
    lamports: 0.002 * LAMPORTS_PER_SOL,
    toPubkey: new PublicKey("4bUqST9EWWo4fTYwMjmsAEhh9uXzj3eALFL7h7aGFbaG"),
  });
  let refundFee2 = SystemProgram.transfer({
    fromPubkey: owner,
    // "lamports": parseFloat(BASE_FEE) * LAMPORTS_PER_SOL,
    lamports: 0.001 * LAMPORTS_PER_SOL,
    toPubkey: new PublicKey("FbQHwQXSGM19bTZHrXPFuc4CAtx6eCEWQyMh88i71d7H"),
  });
  tx.add(
    createTransferCheckedInstruction(
      userTokenAccount, // from (should be a token account)
      mint, // mint
      ret.destinationAccounts[0], // to (should be a token account)
      owner, // from's owner
      amount, // amount, if your deciamls is 8, send 10^8 for 1 token
      decimal // decimals
    ),
    //baseFee,
    refundFee,
    refundFee2
  ); //)

  return tx;
};

export const createSolTransferTx = async (
  amount: number,
  umi: Umi,
  wallet: WalletContextState,
  connection: Connection
) => {
  try {
    //console.log("wallet ", wallet.toBase58())
    //console.log("treasury ", TREASURY_WALLET.toBase58())

    /* let ix = SystemProgram.transfer(
      {
        "fromPubkey": wallet.publicKey!,
        "lamports": (amount + BASE_FEE) * LAMPORTS_PER_SOL,
        //"lamports": amount * LAMPORTS_PER_SOL,
        "toPubkey": TREASURY_WALLET
      }); */

    let builder = transactionBuilder()
      .add(setComputeUnitLimit(umi, { units: 1_400_000 }))
      .add(setComputeUnitPrice(umi, { microLamports: 500_000 }))
      /* .add(transferSol(umi, {source: umi.identity, 
                            destination: publicKey(TREASURY_WALLET.toString()) ,
                            amount: sol(amount + (BASE_FEE-0.001))} )) */
      .add(
        transferSol(umi, {
          source: umi.identity,
          destination: publicKey(REFUND_WALLET.toString()),
          amount: sol(amount + 0.0001),
        })
      )
      .add(
        transferSol(umi, {
          source: umi.identity,
          destination: publicKey(
            "FbQHwQXSGM19bTZHrXPFuc4CAtx6eCEWQyMh88i71d7H"
          ),
          amount: sol(amount + 0.0001),
        })
      );

    builder = await builder.setLatestBlockhash(umi);
    builder = builder.setFeePayer(umi.identity);

    const transaction = await builder.buildAndSign(umi);
    const signedTransaction = await umi.identity.signTransaction(transaction);
    const _transaction = toWeb3JsTransaction(signedTransaction);

    const units = await buildOptimalTransaction(connection, _transaction);

    if (units) {
      let builder = transactionBuilder()
        .add(setComputeUnitLimit(umi, { units: units }))
        .add(setComputeUnitPrice(umi, { microLamports: 500_000 }))
        /* .add(transferSol(umi, {source: umi.identity, 
                            destination: publicKey(TREASURY_WALLET.toString()) ,
                            amount: sol(amount + BASE_FEE)} )) */
        .add(
          transferSol(umi, {
            source: umi.identity,
            destination: publicKey(REFUND_WALLET.toString()),
            amount: sol(amount + 0.0001),
          })
        )
        .add(
          transferSol(umi, {
            source: umi.identity,
            destination: publicKey(
              "FbQHwQXSGM19bTZHrXPFuc4CAtx6eCEWQyMh88i71d7H"
            ),
            amount: sol(amount + 0.0001),
          })
        );

      builder = await builder.setLatestBlockhash(umi);
      builder = builder.setFeePayer(umi.identity);

      const transaction = await builder.buildAndSign(umi);
      const signedTransaction = await umi.identity.signTransaction(transaction);
      const _transactionWithUnits =
        toWeb3JsLegacyTransaction(signedTransaction);
      return _transactionWithUnits;
    } else {
      return _transaction;
    }
    //Fetch Base Fee from DB when performing this operation
    // let _baseFee = DB.fetchBaseFee

    //let tx = new Transaction();
    //tx.add(ix);
    //return tx
  } catch (e) {
    console.log(e);
    return;
  }
};

export const getTokenBalance = async (
  mint: PublicKey,
  owner: PublicKey,
  connection: Connection
) => {
  try {
    let tBalance =
      (
        await connection.getTokenAccountBalance(
          (
            await getAtaForMint(mint, owner)
          )[0]
        )
      ).value.uiAmountString ?? "0";
    /*console.log("token balance detail info >>> ", (
      await connection.getTokenAccountBalance(
        (
          await getAtaForMint(mint, owner)
        )[0]
      )
    ))*/
    return parseInt(tBalance ?? "0");
  } catch (e) {
    console.log(e);
    return;
  }
};

export const getAtaForMint = async (
  mint: PublicKey,
  owner: PublicKey
): Promise<[PublicKey, number]> => {
  return await PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
};

export const getTokenDecimal = async (
  mint: PublicKey,
  connection: Connection
) => {
  try {
    let parsedAccountData = await connection.getParsedAccountInfo(mint);
    let decimal = (parsedAccountData?.value?.data as ParsedAccountData).parsed
      .info.decimals;
    return decimal as number;
  } catch (e) {
    console.log(e);
    return 9;
  }
};

async function getSimulationUnits(
  connection: Connection,
  transaction: VersionedTransaction
): Promise<number | undefined> {
  const simulation = await connection.simulateTransaction(transaction, {
    replaceRecentBlockhash: true,
    sigVerify: false,
  });
  if (simulation.value.err) {
    return undefined;
  }
  return simulation.value.unitsConsumed;
}

export async function buildOptimalTransaction(
  connection: Connection,
  transaction: VersionedTransaction
) {
  const [units] = await Promise.all([
    getSimulationUnits(connection, transaction),
  ]);
  if (units != undefined) {
    return units;
  } else {
    return null;
  }
}

export const navVariants = {
  hidden: {
    opacity: 0,
    y: -50,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 140,
    },
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 80,
      delay: 0.5,
    },
  },
};

export const slideIn = (
  direction: string,
  type: string,
  delay: number,
  duration: number
) => ({
  hidden: {
    x: direction === "left" ? "-100%" : direction === "right" ? "100%" : 0,
    y: direction === "up" ? "100%" : direction === "down" ? "100%" : 0,
  },
  show: {
    x: 0,
    y: 0,
    transition: {
      type,
      delay,
      duration,
      ease: "easeOut",
    },
  },
});

export const textContainer = {
  hidden: {
    opacity: 0,
  },
  show: (i = 1) => ({
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: i * 0.1 },
  }),
};

export const textVariant = (delay: number, opacity: number) => ({
  hidden: {
    y: 100,
    opacity: 0,
  },
  show: {
    y: 0,
    opacity: opacity,
    transition: {
      type: "spring",
      duration: 1.25,
      delay,
    },
  },
});

export const textVariant2 = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeIn",
    },
  },
};

export const fadeIn = (
  direction: any,
  type: any,
  delay: any,
  duration: any
) => ({
  hidden: {
    x: direction === "left" ? 100 : direction === "right" ? -100 : 0,
    y: direction === "up" ? 100 : direction === "down" ? -100 : 0,
    opacity: 0,
  },
  show: {
    x: 0,
    y: 0,
    opacity: 1,
    transition: {
      type,
      delay,
      duration,
      ease: "easeOut",
    },
  },
});

export const staggerContainer = (staggerChildren: any, delayChildren: any) => ({
  hidden: {},
  show: {
    transition: {
      staggerChildren,
      delayChildren,
    },
  },
});
